import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import plur from 'plur'
import * as React from 'react'
import type {EntryPointProps} from 'react-relay'

import {PlaceIdList} from '../../../../plugins/plugins.types'
import {getFavoriteAgentPoolsHref} from '../../../../routes'
import {getAgentPreviewsReady} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import RowGroup from '../../../common/Grid/RowGroup'
import Loader from '../../../common/Loader/Loader'
import Plugin from '../../../common/Plugin/Plugin'
import Redirect from '../../../common/Redirect/Redirect'
import {AgentPagePoolsSubscriber, useAgentPools} from '../AgentPagePoolsProvider'
import type {AgentsPagesQueries} from '../AgentsPages.queries'
import {AgentTreeItemType} from '../AgentsSidebar/AgentsSidebar.types'
import {sortedSelectionsTree} from '../AgentsSidebar/selectors/sortedTree'
import {usePoolsAuthorizePermissionsQueryRef} from '../components/AgentListView/AuthorizeAgent/AuthorizeAgent.hooks'

import VisibleUnauthorizedAgent from './VisibleUnauthorizedAgent/VisibleUnauthorizedAgent'

import styles from './UnauthorizedAgentsPage.css'

import {useAppSelector} from 'src/hooks/react-redux'

export default function UnauthorizedAgentsPageContainer({
  queries,
}: EntryPointProps<AgentsPagesQueries, {}, {}, {}>) {
  return (
    <AgentPagePoolsSubscriber agentTypesQueryRef={queries.agentTypes}>
      <UnauthorizedAgentsPage />
    </AgentPagePoolsSubscriber>
  )
}

function UnauthorizedAgentsPage() {
  const [poolsAuthorizePermissionsQueryRef] = usePoolsAuthorizePermissionsQueryRef()
  const ready = useAppSelector(state => getAgentPreviewsReady(state))
  const pools = useAgentPools()
  const [agentSelectionTree] = useAppSelector(state =>
    sortedSelectionsTree(state, {
      pools,
    }),
  )
  const {agentsLeft} = restApi.endpoints.getLicensingData.useQuery(
    {},
    {
      selectFromResult: ({data}) => {
        const {agentsLeft} = data ?? {}
        return {agentsLeft}
      },
    },
  )
  const areAgentsUnlimited = agentsLeft === -1

  if (agentSelectionTree == null) {
    if (ready) {
      return <Redirect to={getFavoriteAgentPoolsHref()} />
    }

    return <Loader />
  }

  const label = `${agentsLeft} agent ${plur('license', agentsLeft)} ${plur(
    'is',
    'are',
    agentsLeft ?? 0,
  )} available`

  return (
    <div className={styles.page}>
      <H1 className={styles.heading}>{'Unauthorized agents'}</H1>
      <Plugin placeId={PlaceIdList.SAKURA_BEFORE_CONTENT} />
      {!areAgentsUnlimited && agentsLeft != null ? (
        <div className={styles.licensesText}>{label}</div>
      ) : null}
      {poolsAuthorizePermissionsQueryRef != null ? (
        <div className={styles.agents}>
          <RowGroup>
            {agentSelectionTree.children.map(agentNode =>
              agentNode.type === AgentTreeItemType.AGENT ? (
                <VisibleUnauthorizedAgent
                  key={agentNode.id}
                  className={styles.agent}
                  poolsAuthorizePermissionsQueryRef={poolsAuthorizePermissionsQueryRef}
                  {...agentNode}
                />
              ) : null,
            )}
          </RowGroup>
        </div>
      ) : null}
    </div>
  )
}
